import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Star, Crown } from 'lucide-react';

// Premium Gift Full-Screen Animations
// Castle (500 diamonds) - Medieval castle with fireworks
// Planet (1000 diamonds) - Galaxy explosion with orbiting stars

// Particle component for effects
const Particle = ({ delay, duration, startX, startY, endX, endY, size, color, type = 'circle' }) => (
  <motion.div
    initial={{ 
      x: startX, 
      y: startY, 
      opacity: 0, 
      scale: 0 
    }}
    animate={{ 
      x: endX, 
      y: endY, 
      opacity: [0, 1, 1, 0], 
      scale: [0, 1.5, 1, 0],
      rotate: type === 'star' ? [0, 180, 360] : 0
    }}
    transition={{ 
      duration, 
      delay, 
      ease: 'easeOut' 
    }}
    className="absolute pointer-events-none"
    style={{
      width: size,
      height: size,
      borderRadius: type === 'circle' ? '50%' : '0',
      background: type === 'star' ? 'transparent' : color,
    }}
  >
    {type === 'star' && (
      <Star className="w-full h-full" style={{ color }} fill={color} />
    )}
    {type === 'sparkle' && (
      <Sparkles className="w-full h-full" style={{ color }} />
    )}
  </motion.div>
);

// Firework burst effect
const Firework = ({ x, y, color, delay = 0 }) => {
  const particles = [];
  const count = 12;
  
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2;
    const distance = 150 + Math.random() * 100;
    particles.push({
      endX: Math.cos(angle) * distance,
      endY: Math.sin(angle) * distance,
      size: 8 + Math.random() * 8,
      delay: delay + Math.random() * 0.2,
    });
  }

  return (
    <div className="absolute" style={{ left: x, top: y }}>
      {particles.map((p, i) => (
        <Particle
          key={i}
          startX={0}
          startY={0}
          endX={p.endX}
          endY={p.endY}
          size={p.size}
          color={color}
          delay={p.delay}
          duration={1.5}
        />
      ))}
    </div>
  );
};

// Castle Gift Animation - Medieval theme with fireworks and golden particles
export const CastleGiftAnimation = ({ sender, onComplete }) => {
  const [showText, setShowText] = useState(false);

  useEffect(() => {
    const textTimer = setTimeout(() => setShowText(true), 500);
    const completeTimer = setTimeout(onComplete, 6000);
    
    return () => {
      clearTimeout(textTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  // Generate random firework positions
  const fireworks = [
    { x: '20%', y: '30%', color: '#FFD700', delay: 0 },
    { x: '80%', y: '25%', color: '#FF6B6B', delay: 0.3 },
    { x: '50%', y: '20%', color: '#00F0FF', delay: 0.6 },
    { x: '30%', y: '50%', color: '#FFE800', delay: 0.9 },
    { x: '70%', y: '45%', color: '#A855F7', delay: 1.2 },
    { x: '15%', y: '60%', color: '#FF2A2A', delay: 1.5 },
    { x: '85%', y: '55%', color: '#00FF66', delay: 1.8 },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] pointer-events-none overflow-hidden"
      data-testid="castle-gift-animation"
    >
      {/* Dark overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.7 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black"
      />

      {/* Fireworks */}
      {fireworks.map((fw, i) => (
        <Firework key={i} x={fw.x} y={fw.y} color={fw.color} delay={fw.delay} />
      ))}

      {/* Castle emoji with entrance */}
      <motion.div
        initial={{ scale: 0, y: 100, rotate: -10 }}
        animate={{ 
          scale: [0, 1.5, 1.2, 1.3, 1.2],
          y: [100, -50, 0, -20, 0],
          rotate: [-10, 5, -5, 3, 0]
        }}
        transition={{ duration: 1.5, ease: 'easeOut' }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      >
        <span className="text-[150px] sm:text-[200px] filter drop-shadow-2xl">🏰</span>
        
        {/* Golden glow */}
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.8, 0.5]
          }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute inset-0 rounded-full bg-[#FFD700]/30 blur-3xl -z-10"
        />
      </motion.div>

      {/* Sender info */}
      <AnimatePresence>
        {showText && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -50 }}
            className="absolute bottom-32 left-1/2 -translate-x-1/2 text-center"
          >
            <div className="flex items-center justify-center gap-3 mb-3">
              <Crown className="w-8 h-8 text-[#FFD700]" />
              <span className="text-3xl font-black text-white drop-shadow-lg">CASTLE!</span>
              <Crown className="w-8 h-8 text-[#FFD700]" />
            </div>
            <motion.p
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 0.5, repeat: Infinity }}
              className="text-xl text-[#FFD700] font-bold"
            >
              {sender?.name || 'Anonymous'} sent a Castle!
            </motion.p>
            <div className="flex items-center justify-center gap-2 mt-2">
              <span className="text-4xl">💎</span>
              <span className="text-2xl font-black text-[#00F0FF]">500</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating golden particles */}
      {[...Array(30)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ 
            x: Math.random() * window.innerWidth, 
            y: window.innerHeight + 50,
            opacity: 0
          }}
          animate={{ 
            y: -100,
            opacity: [0, 1, 1, 0],
            rotate: [0, 360]
          }}
          transition={{ 
            duration: 3 + Math.random() * 2,
            delay: Math.random() * 2,
            repeat: 1
          }}
          className="absolute"
        >
          <Star className="w-6 h-6 text-[#FFD700]" fill="#FFD700" />
        </motion.div>
      ))}
    </motion.div>
  );
};

// Planet Gift Animation - Galaxy explosion with orbiting elements
export const PlanetGiftAnimation = ({ sender, onComplete }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const phase1 = setTimeout(() => setPhase(1), 500);
    const phase2 = setTimeout(() => setPhase(2), 1500);
    const complete = setTimeout(onComplete, 8000);
    
    return () => {
      clearTimeout(phase1);
      clearTimeout(phase2);
      clearTimeout(complete);
    };
  }, [onComplete]);

  // Orbiting stars
  const orbitingStars = [...Array(8)].map((_, i) => ({
    angle: (i / 8) * 360,
    distance: 180 + (i % 2) * 40,
    size: 20 + Math.random() * 15,
    speed: 3 + Math.random() * 2,
    color: ['#00F0FF', '#A855F7', '#FF2A2A', '#FFE800', '#00FF66'][i % 5]
  }));

  // Galaxy particles
  const galaxyParticles = [...Array(50)].map((_, i) => ({
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: 2 + Math.random() * 4,
    delay: Math.random() * 2,
    color: ['#00F0FF', '#A855F7', '#FF69B4', '#FFE800'][Math.floor(Math.random() * 4)]
  }));

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] pointer-events-none overflow-hidden"
      data-testid="planet-gift-animation"
    >
      {/* Deep space background */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.9 }}
        className="absolute inset-0 bg-gradient-to-b from-[#0a0015] via-[#1a0030] to-[#000020]"
      />

      {/* Galaxy particles / stars */}
      {galaxyParticles.map((p, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ 
            opacity: [0, 1, 1, 0.5, 1],
            scale: [0, 1, 0.8, 1.2, 1]
          }}
          transition={{ 
            duration: 3,
            delay: p.delay,
            repeat: Infinity,
            repeatType: 'reverse'
          }}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            boxShadow: `0 0 ${p.size * 2}px ${p.color}`
          }}
        />
      ))}

      {/* Central planet with rings */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        {/* Outer glow rings */}
        <motion.div
          animate={{ 
            scale: [1, 1.5, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute inset-0 -m-32 rounded-full border-4 border-[#A855F7]/30"
        />
        <motion.div
          animate={{ 
            scale: [1.2, 1.8, 1.2],
            opacity: [0.2, 0.4, 0.2],
            rotate: [0, 180, 360]
          }}
          transition={{ duration: 4, repeat: Infinity }}
          className="absolute inset-0 -m-48 rounded-full border-2 border-[#00F0FF]/20"
        />

        {/* Planet emoji */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ 
            scale: phase >= 1 ? [0, 1.8, 1.4, 1.5] : 0,
            rotate: phase >= 1 ? [-180, 20, -10, 0] : -180
          }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
        >
          <span className="text-[180px] sm:text-[220px] block filter drop-shadow-2xl">🪐</span>
          
          {/* Radial glow */}
          <motion.div
            animate={{ 
              scale: [1, 1.3, 1],
              opacity: [0.4, 0.7, 0.4]
            }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="absolute inset-0 rounded-full bg-gradient-radial from-[#A855F7]/50 via-[#00F0FF]/30 to-transparent blur-2xl -z-10"
          />
        </motion.div>

        {/* Orbiting stars */}
        {phase >= 1 && orbitingStars.map((star, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: 1,
              rotate: [star.angle, star.angle + 360]
            }}
            transition={{ 
              rotate: { duration: star.speed, repeat: Infinity, ease: 'linear' },
              opacity: { duration: 0.5 }
            }}
            className="absolute top-1/2 left-1/2"
            style={{ 
              width: star.distance * 2, 
              height: star.distance * 2,
              marginLeft: -star.distance,
              marginTop: -star.distance
            }}
          >
            <motion.div
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 0.5, repeat: Infinity }}
              className="absolute"
              style={{ 
                top: 0, 
                left: '50%',
                transform: 'translateX(-50%)'
              }}
            >
              <Star 
                className="filter drop-shadow-lg" 
                style={{ 
                  width: star.size, 
                  height: star.size, 
                  color: star.color,
                  filter: `drop-shadow(0 0 10px ${star.color})`
                }} 
                fill={star.color}
              />
            </motion.div>
          </motion.div>
        ))}
      </div>

      {/* Sender info */}
      <AnimatePresence>
        {phase >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -50 }}
            className="absolute bottom-24 left-1/2 -translate-x-1/2 text-center"
          >
            <motion.div 
              animate={{ 
                textShadow: [
                  '0 0 20px #A855F7',
                  '0 0 40px #00F0FF',
                  '0 0 20px #A855F7'
                ]
              }}
              transition={{ duration: 1, repeat: Infinity }}
              className="flex items-center justify-center gap-4 mb-4"
            >
              <span className="text-4xl">✨</span>
              <span className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#A855F7] via-[#00F0FF] to-[#FF69B4]">
                PLANET!
              </span>
              <span className="text-4xl">✨</span>
            </motion.div>
            
            <motion.p
              animate={{ opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="text-2xl text-white font-bold mb-3"
            >
              {sender?.name || 'Anonymous'} sent a Planet!
            </motion.p>
            
            <motion.div 
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 0.8, repeat: Infinity }}
              className="flex items-center justify-center gap-3"
            >
              <span className="text-5xl">💎</span>
              <span className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#00F0FF] to-[#A855F7]">
                1,000
              </span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Shooting stars */}
      {[...Array(5)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ 
            x: -100, 
            y: Math.random() * window.innerHeight * 0.5,
            opacity: 0
          }}
          animate={{ 
            x: window.innerWidth + 100,
            y: Math.random() * window.innerHeight,
            opacity: [0, 1, 1, 0]
          }}
          transition={{ 
            duration: 1.5,
            delay: 1 + i * 0.8,
            ease: 'easeIn'
          }}
          className="absolute"
        >
          <div className="flex items-center">
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-white to-white rounded-full" />
            <Star className="w-4 h-4 text-white -ml-2" fill="white" />
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

// Main Premium Gift Effects Manager
const PremiumGiftEffects = ({ activeGift, sender, onComplete }) => {
  const handleComplete = useCallback(() => {
    onComplete?.();
  }, [onComplete]);

  return (
    <AnimatePresence>
      {activeGift === 'castle' && (
        <CastleGiftAnimation sender={sender} onComplete={handleComplete} />
      )}
      {activeGift === 'planet' && (
        <PlanetGiftAnimation sender={sender} onComplete={handleComplete} />
      )}
    </AnimatePresence>
  );
};

// Hook for managing premium gift effects
export const usePremiumGiftEffects = () => {
  const [activeEffect, setActiveEffect] = useState(null);
  const [effectSender, setEffectSender] = useState(null);
  const [effectQueue, setEffectQueue] = useState([]);

  const triggerEffect = useCallback((giftId, sender) => {
    if (giftId === 'castle' || giftId === 'planet') {
      setEffectQueue(prev => [...prev, { giftId, sender }]);
    }
  }, []);

  // Process queue
  useEffect(() => {
    if (!activeEffect && effectQueue.length > 0) {
      const next = effectQueue[0];
      setActiveEffect(next.giftId);
      setEffectSender(next.sender);
      setEffectQueue(prev => prev.slice(1));
    }
  }, [activeEffect, effectQueue]);

  const clearEffect = useCallback(() => {
    setActiveEffect(null);
    setEffectSender(null);
  }, []);

  return {
    activeEffect,
    effectSender,
    triggerEffect,
    clearEffect,
    PremiumGiftEffects: () => (
      <PremiumGiftEffects 
        activeGift={activeEffect} 
        sender={effectSender} 
        onComplete={clearEffect} 
      />
    )
  };
};

export default PremiumGiftEffects;
